/**
 * Encryption toggle button shown in the nav bar.
 * Clicking it enables/disables E2EE for outgoing 1:1 messages.
 */

import { Button } from "@/components/ui/button";
import { useEncryption } from "@/hooks/useEncryption";
import { Shield, ShieldCheck, ShieldAlert, ShieldOff } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  toggleEncryption,
  toggleGroupEncryption,
  selectEncryptionEnabled,
  selectGroupEncryptionEnabled,
  selectConversations,
  selectCurrConversation,
  selectConversationEncryptionReady,
} from "@/features/control/controlSlice";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";
import { isExternalDmAddress } from "@/utils/addresses";
import {
  ENCRYPTION_STATUS_AWAITING_CONTACT_KEY,
  ENCRYPTION_STATUS_AWAITING_GROUP_KEY,
  ENCRYPTION_STATUS_ERROR,
  ENCRYPTION_STATUS_EXTERNAL,
  ENCRYPTION_STATUS_GROUP_OFF,
  ENCRYPTION_STATUS_GROUP_ON,
  ENCRYPTION_STATUS_INACTIVE,
  ENCRYPTION_STATUS_INITIALIZING,
  ENCRYPTION_STATUS_OFF,
  ENCRYPTION_STATUS_ON,
  ENCRYPTION_STATUS_UNAVAILABLE,
  ENCRYPTION_FAILURE_REASON,
} from "@/constants/ui";

const EncryptionSettings = () => {
  const dispatch = useAppDispatch();
  const { status } = useEncryption();
  const encryptionEnabled = useAppSelector(selectEncryptionEnabled);
  const groupEncryptionEnabled = useAppSelector(selectGroupEncryptionEnabled);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations = useAppSelector(selectConversations);
  const currConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
  const isGroupConversation = currConv?.type === "Group";
  const conversationEncryptionReady = useAppSelector(selectConversationEncryptionReady);

  if (!routerE2eeEnabled) {
    return (
      <Button variant="ghost" size="sm" className="gap-2" disabled title={ENCRYPTION_STATUS_UNAVAILABLE}>
        <Shield className="h-5 w-5 text-neutral-400" />
        <span className="hidden md:inline">{ENCRYPTION_STATUS_UNAVAILABLE}</span>
      </Button>
    );
  }

  // Our own keys failed for good (status.failureReason): sends would hit the "not ready" dialog
  // every time, so the toggle must stay reachable to turn encryption off; the tooltip says why.
  const failureText = status.failureReason ? ENCRYPTION_FAILURE_REASON[status.failureReason] : null;

  // Group conversations: show a toggleable button once the key is ready.
  // While the key is still being distributed, show a non-interactive waiting state.
  if (isGroupConversation) {
    if (!conversationEncryptionReady && !failureText) {
      return (
        <Button variant="ghost" size="sm" className="gap-2" disabled title={ENCRYPTION_STATUS_AWAITING_GROUP_KEY}>
          <ShieldAlert className="h-5 w-5 text-yellow-500" />
          <span className="hidden md:inline">{ENCRYPTION_STATUS_AWAITING_GROUP_KEY}</span>
        </Button>
      );
    }
    const failed = groupEncryptionEnabled && !!failureText;
    const icon = !groupEncryptionEnabled
      ? <ShieldOff className="h-5 w-5 text-neutral-400" />
      : failed
        ? <ShieldAlert className="h-5 w-5 text-yellow-500" />
        : <ShieldCheck className="h-5 w-5 text-green-500" />;
    const label = !groupEncryptionEnabled
      ? ENCRYPTION_STATUS_GROUP_OFF
      : failed ? ENCRYPTION_STATUS_ERROR : ENCRYPTION_STATUS_GROUP_ON;
    return (
      <Button variant="ghost" size="sm" className="gap-2" onClick={() => dispatch(toggleGroupEncryption())} title={(failed && failureText) || label}>
        {icon}
        <span className="hidden md:inline">{label}</span>
      </Button>
    );
  }

  // Email / federation threads are never encrypted — say so instead of offering a toggle.
  if (currConv && isExternalDmAddress(currConv.address)) {
    return (
      <Button variant="ghost" size="sm" className="gap-2" disabled title={ENCRYPTION_STATUS_EXTERNAL}>
        <ShieldOff className="h-5 w-5 text-neutral-400" />
        <span className="hidden md:inline">{ENCRYPTION_STATUS_EXTERNAL}</span>
      </Button>
    );
  }

  // Our key is ready but this contact's isn't: a send would hit the "key not ready" dialog.
  // Still clickable — turning encryption off is how to send plaintext without that prompt.
  const awaitingContactKey = !!currConv && !conversationEncryptionReady;

  const getIcon = () => {
    if (status.isLoading) {
      return <Shield className="h-5 w-5 animate-pulse" />;
    }
    if (!encryptionEnabled) {
      return <ShieldOff className="h-5 w-5 text-neutral-400" />;
    }
    if (!status.isInitialized || awaitingContactKey) {
      return <ShieldAlert className="h-5 w-5 text-yellow-500" />;
    }
    return <ShieldCheck className="h-5 w-5 text-green-500" />;
  };

  const getLabel = () => {
    if (status.isLoading) return ENCRYPTION_STATUS_INITIALIZING;
    if (!encryptionEnabled) return ENCRYPTION_STATUS_OFF;
    if (!status.isInitialized) return status.error ? ENCRYPTION_STATUS_ERROR : ENCRYPTION_STATUS_INACTIVE;
    return awaitingContactKey ? ENCRYPTION_STATUS_AWAITING_CONTACT_KEY : ENCRYPTION_STATUS_ON;
  };

  // Toggleable once initialized — or once init has failed for good, so encryption can be turned off.
  const canToggle = status.isInitialized || !!failureText;

  const handleClick = () => {
    if (!canToggle) return;
    dispatch(toggleEncryption());
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="gap-2"
      onClick={handleClick}
      disabled={status.isLoading || !canToggle}
      title={(encryptionEnabled && failureText) || getLabel()}
    >
      {getIcon()}
      <span className="hidden md:inline">{getLabel()}</span>
    </Button>
  );
};

export default EncryptionSettings;
